/**
 * WHAT: Oracle user access report component with filtering, pagination, and privilege drilldown.
 * WHY:  Oracle DBAs and compliance teams need to inspect per-user privilege levels, risk ratings,
 *       and account statuses across Oracle databases to support attestation and audit workflows.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { useSafeTimeout } from '../hooks';
import { WideModal } from './common';
import './OracleUserAccessReport.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Renders a filterable, paginated report of Oracle database users and their privileges.
 * WHY:  Enables reviewers to assess Oracle user access by database, collection date, risk level,
 *       and account status, with modal drilldowns into specific privilege grants for audit evidence.
 */
const OracleUserAccessReport = () => {
  const [databases, setDatabases] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [privilegeDetail, setPrivilegeDetail] = useState(null); // For privilege drilldown modal
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collectionDates, setCollectionDates] = useState([]);

  // Pagination — server-side. Backend returns total_count/total_pages for
  // the current filter set; the client tracks current page + size only.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    database_id: '',
    collection_date: '',
    risk_level: '',
    account_status: '',
    user_type: '', // Internal | Individual | Application (blank = all)
    username: '',
    privilege_level: '',
    min_critical_privs: 0,
    min_high_risk_privs: 0
  });

  // Active privilege tiers for the matrix-driven dropdown. Disabling a tier
  // for Oracle in the privilege matrix removes it from this list.
  const [activeTiers, setActiveTiers] = useState([]);
  const [certStatus, setCertStatus] = useState(null);
  // Derived-level drill-down: which component privileges/roles produced a
  // user's level (mirrors the Roles-tab "Derived Level" modal).
  const [levelBreakdown, setLevelBreakdown] = useState(null);
  // Risk-level drill-down: the 4-step CIS/NIST pipeline (privilege → dormancy
  // → status → tech booster) that produced a user's risk badge.
  const [riskBreakdown, setRiskBreakdown] = useState(null);

  // Debounce timer for username filter
  const usernameDebounceTimer = useRef(null);

  // Safe timeout hook for delayed operations
  const { setSafeTimeout } = useSafeTimeout();

  // Fetch databases on mount
  useEffect(() => {
    fetchDatabases();
    fetchSummary();
    fetchUsers();
    fetchActiveTiers();
    fetchCertificationStatus();
  }, []);

  // Reuses the shared check-certification endpoint (same source as the Manage
  // Privilege Mappings banner) so the numbers here are consistent with that
  // screen. Best-effort: a failure just hides the warning, never blocks the
  // report. Oracle predefined roles are managed GLOBALLY (Phase 1), so the
  // role counts here reflect the global PREDEFINED_ROLE refs.
  const fetchCertificationStatus = async () => {
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: 'ORACLE' } }
      );
      if (response.data?.status === 'success') {
        setCertStatus(response.data);
      }
    } catch (e) {
      setCertStatus(null);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (usernameDebounceTimer.current) {
        clearTimeout(usernameDebounceTimer.current);
      }
    };
  }, []);

  const fetchDatabases = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('oracle-access/databases/'));
      setDatabases(response.data.databases || []);
    } catch (err) {
      console.error('Error fetching databases:', err);
    }
  };

  const fetchActiveTiers = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-matrix/oracle/active-tiers/'));
      if (response.data?.success) {
        setActiveTiers(response.data.tiers || []);
      }
    } catch (err) {
      console.error('Failed to fetch active tiers for Oracle:', err);
    }
  };

  const fetchCollectionDates = async (databaseId = '') => {
    try {
      const params = databaseId ? `?database_id=${databaseId}` : '';
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`oracle-access/collection-dates/${params}`));
      setCollectionDates(response.data.collection_dates || []);
    } catch (err) {
      console.error('Error fetching collection dates:', err);
      setCollectionDates([]);
    }
  };

  const fetchSummary = async (databaseId = '') => {
    try {
      const params = databaseId ? `?database_id=${databaseId}` : '';
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`oracle-access/summary/${params}`));
      setSummary(response.data.summary || {});
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const fetchUsers = async (overrideFilters = null, overridePage = null, overrideSize = null) => {
    setLoading(true);
    setError(null);
    try {
      const f = overrideFilters || filters;
      const p = overridePage ?? currentPage;
      const s = overrideSize ?? pageSize;
      const payload = { ...f, page: p, page_size: s };
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('oracle-access/report/'), payload);
      setUsers(response.data.users || []);
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (err) {
      // Clear the report so a failed load (e.g. an uncollected/removed DB) shows
      // an empty report — NOT the previously-selected database's stale rows.
      setUsers([]);
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.error || 'Failed to fetch user access report');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (username) => {
    setLoading(true);
    try {
      const encodedUsername = encodeURIComponent(username);
      const queryParams = filters.database_id ? `?database_id=${filters.database_id}` : '';
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`oracle-access/user/${encodedUsername}/`) + queryParams);

      // Parse JSON strings into objects/arrays
      const user = response.data.user;
      if (user.system_privileges && typeof user.system_privileges === 'string') {
        user.system_privileges = JSON.parse(user.system_privileges);
      }
      if (user.object_privileges && typeof user.object_privileges === 'string') {
        user.object_privileges = JSON.parse(user.object_privileges);
      }
      if (user.roles && typeof user.roles === 'string') {
        user.roles = JSON.parse(user.roles);
      }
      if (user.stats && typeof user.stats === 'string') {
        user.stats = JSON.parse(user.stats);
      }
      if (user.user_details && typeof user.user_details === 'string') {
        user.user_details = JSON.parse(user.user_details);
      }
      if (user.proxy_connections && typeof user.proxy_connections === 'string') {
        user.proxy_connections = JSON.parse(user.proxy_connections);
      }
      if (user.profile_limits && typeof user.profile_limits === 'string') {
        user.profile_limits = JSON.parse(user.profile_limits);
      }

      setSelectedUser(user);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  // Open the derived-level breakdown for one user: the direct system/object
  // privileges and granted roles that produced the user's level (MAX rule,
  // matrix-clamped). Scopes to the row's database so the breakdown matches the
  // level shown in that row.
  const openLevelBreakdown = async (user) => {
    setLevelBreakdown({ username: user.username, loading: true, data: null });
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/user-component-levels/'),
        { params: { database_type: 'ORACLE', username: user.username, database_name: user.db_name, dbid: user.dbid || undefined } }
      );
      setLevelBreakdown({ username: user.username, loading: false, data: resp.data });
    } catch (e) {
      setLevelBreakdown({ username: user.username, loading: false, data: null, error: e.response?.data?.message || e.message });
    }
  };

  // Open the Risk breakdown for one user: the 4-step CIS/NIST pipeline that
  // produced this user's risk tier. Same source-row scoping as the level
  // breakdown so the trace agrees with what the report row shows.
  const openRiskBreakdown = async (user) => {
    setRiskBreakdown({ username: user.username, loading: true, data: null });
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/oracle-user-risk-breakdown/'),
        { params: { database_type: 'ORACLE', username: user.username, database_name: user.db_name, dbid: user.dbid || undefined } }
      );
      setRiskBreakdown({ username: user.username, loading: false, data: resp.data });
    } catch (e) {
      setRiskBreakdown({ username: user.username, loading: false, data: null, error: e.response?.data?.message || e.message });
    }
  };

  // Server-side pagination: render whatever the server returned for this page.
  // startIndex/endIndex are display-only values for "Showing X-Y of N" copy.
  const currentUsers = users;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + users.length, totalCount);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchUsers(filters, newPage, pageSize);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchUsers(filters, 1, newSize);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };

    // When database changes, re-fetch collection dates and reset collection_date
    if (name === 'database_id') {
      newFilters.collection_date = '';
      fetchCollectionDates(value);
    }

    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filter changes

    // Handle username field with debouncing for dynamic filtering
    if (name === 'username') {
      // Clear existing timer
      if (usernameDebounceTimer.current) {
        clearTimeout(usernameDebounceTimer.current);
      }

      // Set new timer to trigger filter after 500ms delay
      usernameDebounceTimer.current = setSafeTimeout(() => {
        fetchUsersWithFilters(newFilters);
      }, 500);
    } else {
      // Auto-apply filters immediately for dropdown changes
      setSafeTimeout(() => {
        fetchUsersWithFilters(newFilters);
        if (name === 'database_id') {
          fetchSummary(value);
        }
      }, 100);
    }
  };

  const fetchUsersWithFilters = async (filtersToUse = filters) => {
    setLoading(true);
    setError(null);
    try {
      // Filter changes always reset to page 1.
      const payload = { ...filtersToUse, page: 1, page_size: pageSize };
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('oracle-access/report/'), payload);
      setUsers(response.data.users || []);
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
      setCurrentPage(1);
    } catch (err) {
      // Clear the report so a failed load (e.g. an uncollected/removed DB) shows
      // an empty report — NOT the previously-selected database's stale rows.
      setUsers([]);
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.error || 'Failed to fetch user access report');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    fetchUsers();
    if (filters.database_id) {
      fetchSummary(filters.database_id);
    } else {
      fetchSummary();
    }
  };

  const resetFilters = () => {
    const defaultFilters = {
      database_id: '',
      collection_date: '',
      risk_level: '',
      account_status: '',
      user_type: '', // Show all users
      username: '',
      privilege_level: '',
      min_critical_privs: 0,
      min_high_risk_privs: 0
    };
    setFilters(defaultFilters);
    setCollectionDates([]);
    setCurrentPage(1); // Reset to first page
    setSafeTimeout(() => {
      fetchUsersWithFilters(defaultFilters);
      fetchSummary();
    }, 100);
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('oracle-access/export/csv/'),
        filters,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `oracle_access_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV');
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('oracle-access/export/pdf/'),
        filters,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `oracle_access_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF');
    }
  };

  const formatStatLabel = (key) => {
    // Convert snake_case to Title Case with proper formatting
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getRiskBadgeClass = (riskLevel) => {
    switch (riskLevel) {
      case 'CRITICAL': return 'text-danger fw-bold';
      case 'HIGH': return 'text-warning fw-bold';
      case 'MEDIUM': return 'text-info';
      case 'LOW': return 'text-secondary';
      default: return 'text-muted';
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'OPEN') return 'text-success';
    if (status.includes('LOCKED')) return 'text-danger';
    if (status.includes('EXPIRED')) return 'text-warning';
    return 'text-muted';
  };

  const getRiskExplanation = (user) => {
    // Parse stats if it's a string
    const stats = typeof user.stats === 'string' ? JSON.parse(user.stats) : user.stats;

    // Use the row's matrix-clamped level (single source of truth) so the risk
    // tooltip matches the badge. Falls back to the unclamped stat only if the
    // row level is somehow absent.
    const privilegeLevel = user.privilege_level_name || stats?.max_privilege_level_name || 'UNASSIGNED';
    const accountStatus = user.account_status || 'UNKNOWN';
    const hasProxyGrant = stats?.has_proxy_grant || false;
    const lastLogin = user.last_login;

    // Calculate days since last login
    let loginStatus = 'Never logged in';
    let daysSinceLogin = null;
    if (lastLogin) {
      const loginDate = new Date(lastLogin);
      const today = new Date();
      daysSinceLogin = Math.floor((today - loginDate) / (1000 * 60 * 60 * 24));

      if (daysSinceLogin < 90) {
        loginStatus = `Last login: ${daysSinceLogin} days ago`;
      } else if (daysSinceLogin < 365) {
        loginStatus = `Last login: ${daysSinceLogin} days ago (inactive)`;
      } else {
        loginStatus = `Last login: ${daysSinceLogin} days ago (dormant)`;
      }
    }

    // Build explanation
    const factors = [];
    factors.push(`Privilege Level: ${privilegeLevel}`);
    factors.push(`Account Status: ${accountStatus}`);
    factors.push(loginStatus);
    if (hasProxyGrant) {
      factors.push('⚠️ Has Proxy Grant capability');
    }

    return factors.join('\n');
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
  };

  const showPrivilegeDetail = async (username, category, count, grantSource = null) => {
    if (count === 0) return; // Don't show modal for zero counts

    setLoading(true);
    try {
      const encodedUsername = encodeURIComponent(username);
      const queryParams = filters.database_id ? `?database_id=${filters.database_id}` : '';
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`oracle-access/user/${encodedUsername}/`) + queryParams);

      // Parse JSON strings into objects/arrays
      const user = response.data.user;
      if (user.system_privileges && typeof user.system_privileges === 'string') {
        user.system_privileges = JSON.parse(user.system_privileges);
      }
      if (user.object_privileges && typeof user.object_privileges === 'string') {
        user.object_privileges = JSON.parse(user.object_privileges);
      }
      if (user.roles && typeof user.roles === 'string') {
        user.roles = JSON.parse(user.roles);
      }
      if (user.stats && typeof user.stats === 'string') {
        user.stats = JSON.parse(user.stats);
      }

      setPrivilegeDetail({
        username,
        category,
        user,
        count,
        grantSource // 'DIRECT', 'VIA_ROLE', or null for all
      });
    } catch (err) {
      console.error('Error fetching privilege details:', err);
      setError('Failed to fetch privilege details');
    } finally {
      setLoading(false);
    }
  };

  const closePrivilegeDetail = () => {
    setPrivilegeDetail(null);
  };

  // Shared pagination strip — rendered above AND below the table so users can
  // navigate without scrolling. `placement` toggles only the divider border.
  const renderPaginationBar = (placement) => {
    if (totalCount === 0) return null;
    const borderStyle = placement === 'top'
      ? { borderBottom: '1px solid #e5e7eb', marginBottom: '10px' }
      : { borderTop: '1px solid #e5e7eb', marginTop: '20px' };
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px',
        ...borderStyle,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{ fontSize: '14px', color: '#374151' }}>Rows per page:</label>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{
              padding: '5px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '20px' }}>
            Showing {startIndex + 1}-{endIndex} of {totalCount}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
          >
            First
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
          >
            Previous
          </button>
          {[...Array(totalPages)].map((_, i) => {
            const pageNum = i + 1;
            if (
              pageNum === 1 ||
              pageNum === totalPages ||
              (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
            ) {
              return (
                <button
                  key={pageNum}
                  className={`btn btn-small ${currentPage === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    minWidth: '35px',
                    fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                  }}
                >
                  {pageNum}
                </button>
              );
            } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
              return <span key={pageNum} style={{ padding: '0 5px' }}>...</span>;
            }
            return null;
          })}
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
          >
            Next
          </button>
          <button
            className="btn btn-secondary btn-small"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="oracle-access-report">
      <div className="report-header">
        <h2>Oracle User Access Report</h2>
        <p className="report-subtitle">Comprehensive privilege analysis and access review</p>
      </div>

      {/* Privilege-mapping completeness warning. Shown when any Oracle
          privilege or predefined role is UNASSIGNED or uncertified — those
          mappings contribute nothing to a user's computed level, so the
          levels below may be understated. Derived entirely from the shared
          check-certification endpoint (same source as the Manage Privilege
          Mappings banner). */}
      {certStatus && (() => {
        const unassigned = (certStatus.unassigned_count || 0);
        const uncertPriv = (certStatus.uncertified_count || 0);
        const uncertRole = (certStatus.role_uncertified_count || 0);
        const incomplete = unassigned + uncertPriv + uncertRole;
        if (incomplete === 0) return null;
        return (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ color: '#856404', fontSize: '0.95rem', lineHeight: 1.5 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
              <strong>Privilege levels below may be understated.</strong>
              {' '}The Oracle privilege mappings are incomplete:
              {unassigned > 0 && <> <strong>{unassigned}</strong> unassigned privilege{unassigned === 1 ? '' : 's'}</>}
              {unassigned > 0 && (uncertPriv > 0 || uncertRole > 0) ? ',' : ''}
              {uncertPriv > 0 && <> <strong>{uncertPriv}</strong> uncertified privilege{uncertPriv === 1 ? '' : 's'}</>}
              {uncertPriv > 0 && uncertRole > 0 ? ',' : ''}
              {uncertRole > 0 && <> <strong>{uncertRole}</strong> uncertified predefined role{uncertRole === 1 ? '' : 's'}</>}.
              {' '}Unassigned or uncertified mappings do not contribute to a user's
              computed privilege level until a DBA classifies and certifies them in{' '}
              <strong>Manage Privilege Mappings</strong>, so the levels shown here
              may be lower than a user's effective access.
            </div>
          </div>
        );
      })()}

      {/* Compact summary cards — scoped override only for this report so the
          shared .summary-card (used by MySQL/etc.) is untouched. Matches the
          slim density of the MSSQL report and the Privilege Mappings
          workspace. */}
      <style>{`
        .oracle-compact-cards {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px;
        }
        .oracle-compact-cards .summary-card {
          flex: 1 1 96px; padding: 6px 10px; border-radius: 7px;
          box-shadow: none; transition: none;
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 8px;
        }
        .oracle-compact-cards .summary-card:hover { transform: none; box-shadow: none; }
        .oracle-compact-cards .summary-label {
          font-size: 10.5px; margin-bottom: 0; white-space: nowrap;
        }
        .oracle-compact-cards .summary-value { font-size: 17px; }
      `}</style>
      {(() => {
        // Dynamic privilege-level cards. One card per DISTINCT privilege level
        // actually present (count > 0) in this report's privilege_distribution
        // (computed by the backend over the full in-scope collection). Ordered
        // by severity high→low so the riskiest tiers read first. Levels with
        // no users are omitted, so the card set changes with the data. The
        // context cards (Total/App/Common/Open/Locked) stay fixed. Mirrors
        // MSSQLUserAccessReport so both reports read identically.
        const SEVERITY_ORDER = [
          ['SUPER_ADMIN', 'danger'],
          ['ADMIN', 'danger'],
          ['WRITE_ELEVATED', 'warning'],
          ['WRITE', 'warning'],
          ['READ_ELEVATED', 'info'],
          ['READ', 'info'],
          ['NONE', ''],
          ['UNASSIGNED', ''],
        ];
        const dist = summary.privilege_distribution || {};
        const levelCards = SEVERITY_ORDER
          .filter(([name]) => (dist[name] || 0) > 0)
          .map(([name, cls]) => ({ name, cls, count: dist[name] }));
        return (
          <div className="summary-cards oracle-compact-cards">
            <div className="summary-card">
              <div className="summary-label">Total Users</div>
              <div className="summary-value">{summary.total_users || 0}</div>
            </div>
            <div className="summary-card info">
              <div className="summary-label">Application Users</div>
              <div className="summary-value">{summary.app_users || 0}</div>
            </div>
            <div className="summary-card info" title="Container-level users that may have access to multiple PDBs">
              <div className="summary-label">Common Users</div>
              <div className="summary-value">{summary.common_users || 0}</div>
            </div>
            {levelCards.map(c => (
              <div key={c.name} className={`summary-card ${c.cls}`}>
                <div className="summary-label">{c.name.replace(/_/g, ' ')}</div>
                <div className="summary-value">{c.count}</div>
              </div>
            ))}
            <div className="summary-card">
              <div className="summary-label">Open Accounts</div>
              <div className="summary-value">{summary.open_accounts || 0}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Locked Accounts</div>
              <div className="summary-value">{summary.locked_accounts || 0}</div>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Database</label>
            <select name="database_id" value={filters.database_id} onChange={handleFilterChange}>
              <option value="">All Databases</option>
              {databases.map(db => (
                <option key={db.id} value={db.id}>{db.db_name} ({db.instance_name})</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Collection Date</label>
            <select name="collection_date" value={filters.collection_date} onChange={handleFilterChange}>
              <option value="">Latest</option>
              {collectionDates.map((cd, idx) => (
                <option key={idx} value={cd.value}>{cd.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Risk Level</label>
            <select name="risk_level" value={filters.risk_level} onChange={handleFilterChange}>
              <option value="">All Levels</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Privilege Level</label>
            <select name="privilege_level" value={filters.privilege_level} onChange={handleFilterChange}>
              <option value="">All Levels</option>
              {activeTiers.map(t => (
                <option key={t.tier_name} value={t.tier_value}>{t.tier_name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Account Status</label>
            <select name="account_status" value={filters.account_status} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              <option value="OPEN">Open</option>
              <option value="LOCKED">Locked</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>

          <div className="filter-group">
            <label>User Type</label>
            <select name="user_type" value={filters.user_type} onChange={handleFilterChange}>
              <option value="">All Users</option>
              <option value="Internal">Internal</option>
              <option value="Individual">Individual</option>
              <option value="Security Group">Security Group</option>
              <option value="Application Service Account">Application Service Account</option>
              <option value="Unclassified">Unclassified</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Username Search</label>
            <input
              type="text"
              name="username"
              value={filters.username}
              onChange={handleFilterChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Clear debounce timer and apply immediately
                  if (usernameDebounceTimer.current) {
                    clearTimeout(usernameDebounceTimer.current);
                  }
                  applyFilters();
                }
              }}
              placeholder="Type to search username..."
            />
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={resetFilters}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="export-section">
        <button className="btn btn-export" onClick={exportToCSV}>
          📊 Export to CSV
        </button>
        <button className="btn btn-export" onClick={exportToPDF}>
          📄 Export to PDF
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="users-table-container">
          <h3>
            User Access Details
            {totalCount > 0
              ? ` (Showing ${startIndex + 1}-${endIndex} of ${totalCount} users)`
              : ` (0 users)`
            }
          </h3>
          {renderPaginationBar('top')}
          <div className="table-wrapper">
            <table className="table table-hover mb-0 users-table">
              <thead className="table-dark">
                <tr>
                  <th>Database</th>
                  <th>Username</th>
                  <th>User Type</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Sys Privs (Direct)</th>
                  <th>Obj Privs (Direct)</th>
                  <th>Obj Privs (Roles)</th>
                  <th>Roles</th>
                  <th>Critical</th>
                  <th>High Risk</th>
                  <th>Privilege Level</th>
                  <th>Risk Level</th>
                  <th>View Privileges</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user, index) => (
                  <tr key={index} className={user.critical_privs > 0 ? 'critical-row' : ''}>
                    <td>{user.db_name}</td>
                    <td className="username-cell">
                      <div>{user.username}</div>
                      {user.is_common_user && (
                        <div className="text-info" style={{ fontSize: '10px', fontStyle: 'italic' }}>
                          <i className="fas fa-globe me-1" style={{ fontSize: '8px' }}></i>Common
                        </div>
                      )}
                    </td>
                    <td>
                      {user.user_type === 'Internal' ? (
                        <span className="text-secondary" title="System-managed by the database engine"><i className="fas fa-cog fa-xs me-1"></i>Internal</span>
                      ) : user.user_type === 'Individual' ? (
                        <span className="text-success" title="Named user matched in enterprise directory"><i className="fas fa-user fa-xs me-1"></i>Individual</span>
                      ) : user.user_type === 'Security Group' ? (
                        <span className="text-warning" title="Role or group principal (not a real user)"><i className="fas fa-users fa-xs me-1"></i>Security Group</span>
                      ) : user.user_type === 'Application Service Account' ? (
                        <span className="text-info" title="Service account registered in account_owner with a designated attester"><i className="fas fa-cube fa-xs me-1"></i>App Service</span>
                      ) : user.user_type === 'Unclassified' ? (
                        <span className="text-danger" title="Not in any registry — register in database_owners or account_owner"><i className="fas fa-question-circle fa-xs me-1"></i>Unclassified</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(user.account_status)}>
                        {user.account_status}
                      </span>
                    </td>
                    <td>{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'system_privileges', user.sys_privs_direct || 0, 'DIRECT')}
                        style={{ cursor: (user.sys_privs_direct || 0) > 0 ? 'pointer' : 'default', color: (user.sys_privs_direct || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.sys_privs_direct || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.sys_privs_direct || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'object_privileges', user.obj_privs_direct || 0, 'DIRECT')}
                        style={{ cursor: (user.obj_privs_direct || 0) > 0 ? 'pointer' : 'default', color: (user.obj_privs_direct || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.obj_privs_direct || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.obj_privs_direct || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'object_privileges', user.obj_privs_roles || 0, 'VIA_ROLE')}
                        style={{ cursor: (user.obj_privs_roles || 0) > 0 ? 'pointer' : 'default', color: (user.obj_privs_roles || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.obj_privs_roles || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.obj_privs_roles || 0}
                      </span>
                    </td>
                    <td>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'roles', user.roles_direct || 0, 'DIRECT')}
                        style={{ cursor: (user.roles_direct || 0) > 0 ? 'pointer' : 'default', color: (user.roles_direct || 0) > 0 ? '#2563eb' : 'inherit', textDecoration: (user.roles_direct || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.roles_direct || 0}
                      </span>
                    </td>
                    <td className={user.critical_privs > 0 ? 'critical-cell' : ''}>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'critical', user.critical_privs || 0)}
                        style={{ cursor: (user.critical_privs || 0) > 0 ? 'pointer' : 'default', textDecoration: (user.critical_privs || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.critical_privs || 0}
                      </span>
                    </td>
                    <td className={user.high_risk_privs > 0 ? 'high-risk-cell' : ''}>
                      <span
                        className="privilege-link"
                        onClick={() => showPrivilegeDetail(user.username, 'high_risk', user.high_risk_privs || 0)}
                        style={{ cursor: (user.high_risk_privs || 0) > 0 ? 'pointer' : 'default', textDecoration: (user.high_risk_privs || 0) > 0 ? 'underline' : 'none' }}
                      >
                        {user.high_risk_privs || 0}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 border-0"
                        style={{ textDecoration: 'none' }}
                        onClick={() => openLevelBreakdown(user)}
                        title="Show the privileges and roles behind this derived level"
                      >
                        <span className={`privilege-level-badge privilege-level-${(user.privilege_level_name || 'UNASSIGNED').toLowerCase().replace(/_/g, '-')}`}>
                          {user.privilege_level_name || 'UNASSIGNED'}
                        </span>
                        <i className="fas fa-external-link-alt ms-1" style={{ fontSize: '0.7em', opacity: 0.6 }}></i>
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 border-0"
                        style={{ textDecoration: 'none' }}
                        onClick={() => openRiskBreakdown(user)}
                        title={getRiskExplanation(user)}
                      >
                        <span className={getRiskBadgeClass(user.overall_risk_level)}>
                          {user.overall_risk_level}
                        </span>
                        <i className="fas fa-external-link-alt ms-1" style={{ fontSize: '0.7em', opacity: 0.6 }}></i>
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-small"
                        onClick={() => fetchUserDetails(user.username)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {renderPaginationBar('bottom')}
        </div>
      )}

      {/* User Details Modal */}
      <WideModal
        show={!!selectedUser}
        onClose={closeUserDetails}
        title={
          selectedUser ? (
            <>
              <span style={{ color: '#60a5fa', fontWeight: '800' }}>{selectedUser.username}</span>
              {(selectedUser.user_details?.is_common_user || selectedUser.is_common_user) && (
                <span className="badge bg-info ms-2" style={{ fontSize: '0.55em', verticalAlign: 'middle' }}>Common User</span>
              )}
              <span style={{ color: '#d1d5db', fontWeight: '400', marginLeft: '12px' }}>- Access Details</span>
            </>
          ) : ''
        }
        headerIcon="fa-user"
        maxWidth="1100px"
        footer={<button className="btn btn-secondary" onClick={closeUserDetails}>Close</button>}
      >
        {selectedUser && (
          <>
              {/* User Info */}
              <div className="detail-section">
                <h4>User Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Database:</span>
                    <span className="detail-value">{selectedUser.db_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">User Type:</span>
                    <span className="detail-value">
                      {(selectedUser.user_details?.is_common_user || selectedUser.is_common_user) ? (
                        <span className="text-info" title="Container-level user with potential access to multiple PDBs">
                          <i className="fas fa-globe me-1"></i>Common User
                        </span>
                      ) : (
                        <span className="text-secondary">Local User</span>
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Account Status:</span>
                    <span className={getStatusBadgeClass(selectedUser.account_status)}>
                      {selectedUser.account_status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">
                      {selectedUser.created_date ? new Date(selectedUser.created_date).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Last Login:</span>
                    <span className="detail-value">
                      {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="detail-section">
                <h4>Privilege Statistics</h4>
                <div className="stats-grid">
                  {selectedUser.stats && Object.entries(selectedUser.stats)
                    .filter(([key, value]) => typeof value !== 'object' && !Array.isArray(value))
                    .map(([key, value]) => (
                      <div key={key} className="stat-item" style={{ padding: '4px 8px', fontSize: '9px' }}>
                        <span className="stat-label" style={{ fontSize: '9px' }}>{formatStatLabel(key)}:</span>
                        <span className="stat-value" style={{ fontSize: '9px' }}>{value}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* System Privileges */}
              {selectedUser.system_privileges && selectedUser.system_privileges.length > 0 && (
                <div className="detail-section">
                  <h4>System Privileges ({selectedUser.system_privileges.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.system_privileges.slice(0, 20).map((priv, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">{priv.privilege}</span>
                        <span className="priv-source">{priv.grant_source}</span>
                        <span className={getRiskBadgeClass(priv.privilege_risk_level)}>
                          {priv.privilege_risk_level}
                        </span>
                      </div>
                    ))}
                    {selectedUser.system_privileges.length > 20 && (
                      <div className="more-items">...and {selectedUser.system_privileges.length - 20} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Object Privileges */}
              {selectedUser.object_privileges && selectedUser.object_privileges.length > 0 && (
                <div className="detail-section">
                  <h4>Object Privileges ({selectedUser.object_privileges.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.object_privileges.slice(0, 20).map((priv, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">{priv.owner}.{priv.object_name}</span>
                        <span className="priv-type">{priv.privilege}</span>
                        <span className="priv-source" style={{ fontSize: '11px', color: '#6b7280' }}>{priv.grant_type}</span>
                      </div>
                    ))}
                    {selectedUser.object_privileges.length > 20 && (
                      <div className="more-items">...and {selectedUser.object_privileges.length - 20} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Roles */}
              {selectedUser.roles && selectedUser.roles.length > 0 && (
                <div className="detail-section">
                  <h4>Roles ({selectedUser.roles.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.roles.slice(0, 15).map((role, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">{role.granted_role || role.nested_role}</span>
                        <span className="priv-source">{role.grant_level}</span>
                        <span className={getRiskBadgeClass(role.role_risk_level)}>
                          {role.role_risk_level}
                        </span>
                      </div>
                    ))}
                    {selectedUser.roles.length > 15 && (
                      <div className="more-items">...and {selectedUser.roles.length - 15} more</div>
                    )}
                  </div>
                </div>
              )}

              {/* Proxy Connections */}
              {selectedUser.proxy_connections && selectedUser.proxy_connections.length > 0 && (
                <div className="detail-section">
                  <h4>Proxy Connections ({selectedUser.proxy_connections.length})</h4>
                  <div className="privilege-list">
                    {selectedUser.proxy_connections.map((proxy, idx) => (
                      <div key={idx} className="privilege-item">
                        <span className="priv-name">Can proxy as: {proxy.client_user}</span>
                        <span className="priv-source">{proxy.authentication === 'YES' ? 'Auth Required' : 'No Auth'}</span>
                        <span className="risk-badge critical" style={{ fontSize: '10px' }}>HIGH RISK</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </>
        )}
      </WideModal>

      {/* Privilege Detail Modal */}
      <WideModal
        show={!!(privilegeDetail && privilegeDetail.user)}
        onClose={closePrivilegeDetail}
        title={
          privilegeDetail ? (
            <>
              {privilegeDetail.category === 'system_privileges' && 'System Privileges'}
              {privilegeDetail.category === 'object_privileges' && 'Object Privileges'}
              {privilegeDetail.category === 'roles' && 'Roles'}
              {privilegeDetail.category === 'critical' && 'Critical Privileges (SUPER_ADMIN Level 6)'}
              {privilegeDetail.category === 'high_risk' && 'High Risk Privileges (ADMIN Level 5)'}
              {privilegeDetail.category === 'ddl_modify' && 'Write Elevated Privileges (Level 4)'}
              {privilegeDetail.category === 'read_elevated' && 'Read Elevated Privileges (Level 2)'}
              {privilegeDetail.grantSource && ` (${privilegeDetail.grantSource === 'DIRECT' ? 'Direct' : 'Via Roles'})`}
              {' for '}{privilegeDetail.username} ({privilegeDetail.count})
            </>
          ) : ''
        }
        headerIcon="fa-shield-alt"
        maxWidth="900px"
        footer={<button className="btn btn-secondary" onClick={closePrivilegeDetail}>Close</button>}
      >
        {privilegeDetail && privilegeDetail.user && (
          <>
              {/* System Privileges */}
              {privilegeDetail.category === 'system_privileges' && privilegeDetail.user.system_privileges && (
                <div className="privilege-list">
                  {privilegeDetail.user.system_privileges
                    .filter(priv => {
                      if (!privilegeDetail.grantSource) return true;
                      if (privilegeDetail.grantSource === 'DIRECT') return priv.grant_type === 'DIRECT';
                      if (privilegeDetail.grantSource === 'VIA_ROLE') return priv.grant_type === 'VIA_ROLE' || (priv.grant_type && priv.grant_type.startsWith('VIA_ROLE'));
                      return true;
                    })
                    .map((priv, idx) => (
                      <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                        <span className="priv-name" style={{ fontWeight: '500' }}>{priv.privilege}</span>
                        <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>{priv.grant_type}</span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>{priv.admin_option === 'YES' ? 'With Admin' : ''}</span>
                      </div>
                    ))}
                </div>
              )}

              {/* Object Privileges */}
              {privilegeDetail.category === 'object_privileges' && privilegeDetail.user.object_privileges && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '10px', padding: '10px', backgroundColor: '#f9fafb', fontWeight: 'bold', borderBottom: '2px solid #d1d5db' }}>
                      <span>Schema.Object</span>
                      <span>Privilege</span>
                      <span>Grant Type</span>
                      <span>Grantable</span>
                    </div>
                    {privilegeDetail.user.object_privileges
                      .filter(priv => {
                        if (!privilegeDetail.grantSource) return true;
                        if (privilegeDetail.grantSource === 'DIRECT') return priv.grant_type === 'DIRECT';
                        if (privilegeDetail.grantSource === 'VIA_ROLE') return priv.grant_type === 'VIA_ROLE' || (priv.grant_type && priv.grant_type.startsWith('VIA_ROLE'));
                        return true;
                      })
                      .map((priv, idx) => (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span className="priv-name" style={{ fontWeight: '500' }}>{priv.owner}.{priv.object_name}</span>
                          <span className="priv-type" style={{ fontSize: '13px' }}>{priv.privilege}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>{priv.grant_type}</span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>{priv.grantable}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Roles */}
              {privilegeDetail.category === 'roles' && privilegeDetail.user.roles && (
                <div className="privilege-list">
                  {privilegeDetail.user.roles.map((role, idx) => (
                    <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                      <span className="priv-name" style={{ fontWeight: '500' }}>{role.granted_role || role.nested_role}</span>
                      <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>{role.grant_level}</span>
                      <span className={getRiskBadgeClass(role.role_risk_level)}>{role.role_risk_level}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Critical Privileges */}
              {privilegeDetail.category === 'critical' && privilegeDetail.user.stats && (
                <div className="privilege-list">
                  {privilegeDetail.user.stats.critical_privilege_list && privilegeDetail.user.stats.critical_privilege_list.length > 0 ? (
                    privilegeDetail.user.stats.critical_privilege_list.map((privName, idx) => {
                      // Find the privilege in system_privileges to get grant details
                      const priv = privilegeDetail.user.system_privileges?.find(p => p.privilege === privName);
                      return (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span className="priv-name" style={{ fontWeight: '500', color: '#dc2626' }}>{privName}</span>
                          <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>{priv?.grant_type || 'Unknown'}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No critical privileges found</div>
                  )}
                </div>
              )}

              {/* High Risk Privileges */}
              {privilegeDetail.category === 'high_risk' && privilegeDetail.user.stats && (
                <div className="privilege-list">
                  {privilegeDetail.user.stats.high_risk_privilege_list && privilegeDetail.user.stats.high_risk_privilege_list.length > 0 ? (
                    privilegeDetail.user.stats.high_risk_privilege_list.map((privName, idx) => {
                      // Find the privilege in system_privileges to get grant details
                      const priv = privilegeDetail.user.system_privileges?.find(p => p.privilege === privName);
                      return (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span className="priv-name" style={{ fontWeight: '500', color: '#f59e0b' }}>{privName}</span>
                          <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>{priv?.grant_type || 'Unknown'}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No high risk privileges found</div>
                  )}
                </div>
              )}

              {/* Write Elevated Privileges (formerly labeled as DDL Modify) */}
              {privilegeDetail.category === 'ddl_modify' && privilegeDetail.user.stats && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fef3c7', borderLeft: '4px solid #f59e0b', fontSize: '13px' }}>
                    <strong>Note:</strong> These are WRITE_ELEVATED (Level 4) privileges - Elevated DML operations (INSERT/UPDATE/DELETE across objects), not DDL.
                  </div>
                  {privilegeDetail.user.stats.ddl_modify_privilege_list && privilegeDetail.user.stats.ddl_modify_privilege_list.length > 0 ? (
                    privilegeDetail.user.stats.ddl_modify_privilege_list.map((privName, idx) => {
                      // Find the privilege in system_privileges to get grant details
                      const priv = privilegeDetail.user.system_privileges?.find(p => p.privilege === privName);
                      return (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span className="priv-name" style={{ fontWeight: '500' }}>{privName}</span>
                          <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>{priv?.grant_type || 'Unknown'}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No write elevated privileges found</div>
                  )}
                </div>
              )}

              {/* Read Elevated Privileges */}
              {privilegeDetail.category === 'read_elevated' && privilegeDetail.user.stats && (
                <div className="privilege-list">
                  <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#dbeafe', borderLeft: '4px solid #3b82f6', fontSize: '13px' }}>
                    <strong>Note:</strong> These are READ_ELEVATED (Level 2) privileges - Elevated read operations (SELECT across objects, metadata access).
                  </div>
                  {privilegeDetail.user.stats.read_elevated_privilege_list && privilegeDetail.user.stats.read_elevated_privilege_list.length > 0 ? (
                    privilegeDetail.user.stats.read_elevated_privilege_list.map((privName, idx) => {
                      // Find the privilege in system_privileges to get grant details
                      const priv = privilegeDetail.user.system_privileges?.find(p => p.privilege === privName);
                      return (
                        <div key={idx} className="privilege-item" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', padding: '10px', borderBottom: '1px solid #e5e7eb' }}>
                          <span className="priv-name" style={{ fontWeight: '500', color: '#1e40af' }}>{privName}</span>
                          <span className="priv-source" style={{ fontSize: '13px', color: '#6b7280' }}>{priv?.grant_type || 'Unknown'}</span>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No read elevated privileges found</div>
                  )}
                </div>
              )}
          </>
        )}
      </WideModal>

      {/* Derived Level breakdown — the privileges and roles that produced a
          user's level (MAX rule, matrix-clamped). Mirrors the Roles-tab modal. */}
      <WideModal
        show={!!levelBreakdown}
        onClose={() => setLevelBreakdown(null)}
        title={`Derived Level — ${levelBreakdown?.username || ''}`}
        headerIcon="fa-calculator"
        headerClass="bg-dark text-white"
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setLevelBreakdown(null)}>
            Close
          </button>
        }
      >
        {levelBreakdown?.loading && (
          <div className="text-center p-4"><i className="fas fa-spinner fa-spin me-2"></i>Loading breakdown…</div>
        )}
        {levelBreakdown?.error && (
          <div className="alert alert-danger">{levelBreakdown.error}</div>
        )}
        {levelBreakdown?.data && (() => {
          const d = levelBreakdown.data;
          const order = { SUPER_ADMIN: 6, ADMIN: 5, WRITE_ELEVATED: 4, WRITE: 3, READ_ELEVATED: 2, READ: 1, NONE: 0, UNASSIGNED: -1 };
          // Collapse to DISTINCT (component, type, scope, level). The same
          // object privilege (e.g. SELECT) is granted on many objects, so the
          // raw list repeats it dozens of times; for level derivation only the
          // distinct privilege+level matters. A deduped row is a driver if ANY
          // of its occurrences drives the level.
          const dedupMap = new Map();
          for (const c of (d.components || [])) {
            const key = `${c.component}|${c.kind}|${c.scope}|${c.level_name}`;
            const existing = dedupMap.get(key);
            if (existing) {
              existing.is_driver = existing.is_driver || c.is_driver;
            } else {
              dedupMap.set(key, { ...c });
            }
          }
          const rows = [...dedupMap.values()].sort((a, b) => (order[b.level_name] ?? 0) - (order[a.level_name] ?? 0));
          const stale = d.stored_level_name && d.stored_level_name !== d.derived_level_name;
          return (
            <>
              {stale && (
                <div className="alert alert-warning py-2">
                  <i className="fas fa-triangle-exclamation me-2"></i>
                  Stored level is <strong>{d.stored_level_name}</strong> but a live recompute gives <strong>{d.derived_level_name}</strong>.
                  Run <strong>Recategorize</strong> to refresh.
                </div>
              )}
              <div className="d-flex align-items-center flex-wrap gap-3 mb-3 p-2" style={{ background: '#f8f9fa', borderRadius: 6 }}>
                <span className={`privilege-level-badge privilege-level-${(d.derived_level_name || 'NONE').toLowerCase().replace(/_/g, '-')}`}>
                  {d.derived_level_name}
                </span>
                <span className="text-muted">
                  Level = <strong>MAX</strong> of all direct privileges and granted-role levels
                  {d.matrix_capped
                    ? <> — raw <strong>{d.raw_max_level_name}</strong> was clamped to the matrix cap (<strong>{d.matrix_cap_name}</strong>).</>
                    : <>.</>}
                </span>
                <span className="ms-auto text-muted small">
                  {d.database_name} · {d.counts.system} sys · {d.counts.object} obj · {d.counts.roles} role(s)
                </span>
              </div>
              {rows.length === 0 ? (
                <div className="text-muted">No direct privileges or granted roles contribute to this level.</div>
              ) : (
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Component</th>
                      <th>Type</th>
                      <th>Scope</th>
                      <th>Level</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c, i) => (
                      <tr key={i} style={c.is_driver ? { background: '#fff8e1' } : undefined}>
                        <td style={{ fontWeight: 600 }}>{c.component}</td>
                        <td>{c.kind}</td>
                        <td><span className="text-muted">{c.scope}</span></td>
                        <td>
                          <span className={`privilege-level-badge privilege-level-${(c.level_name || 'NONE').toLowerCase().replace(/_/g, '-')}`}>
                            {c.level_name}
                          </span>
                        </td>
                        <td>
                          {c.is_driver && <span className="text-warning"><i className="fas fa-bolt me-1"></i>drives level</span>}
                          {c.unknown && <span className="text-secondary ms-2"><i className="fas fa-question-circle me-1"></i>unknown</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          );
        })()}
      </WideModal>

      {/* Risk Level breakdown — the 4-step CIS/NIST pipeline (privilege →
          dormancy → status → tech booster) that produced the user's risk
          tier. Same shared verbose calculator as the SQL CASE used in the
          report, so the trace agrees bit-for-bit with the badge. */}
      <WideModal
        show={!!riskBreakdown}
        onClose={() => setRiskBreakdown(null)}
        title={`Risk Level — ${riskBreakdown?.username || ''}`}
        headerIcon="fa-shield-halved"
        headerClass="bg-dark text-white"
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setRiskBreakdown(null)}>
            Close
          </button>
        }
      >
        {riskBreakdown?.loading && (
          <div className="text-center p-4"><i className="fas fa-spinner fa-spin me-2"></i>Loading breakdown…</div>
        )}
        {riskBreakdown?.error && (
          <div className="alert alert-danger">{riskBreakdown.error}</div>
        )}
        {riskBreakdown?.data && (() => {
          const d = riskBreakdown.data;
          const ins = d.inputs || {};
          return (
            <>
              <div className="d-flex align-items-center flex-wrap gap-3 mb-3 p-2" style={{ background: '#f8f9fa', borderRadius: 6 }}>
                <span className={getRiskBadgeClass(d.final_risk)} style={{ fontSize: '1.1em' }}>
                  <i className="fas fa-circle fa-xs me-1"></i>{d.final_risk}
                </span>
                <span className="text-muted small">
                  Risk = <strong>base</strong> from privilege level, then <strong>dormancy</strong>, <strong>status</strong>, and <strong>tech-specific boosters</strong> (clamped LOW..CRITICAL).
                </span>
                <span className="ms-auto text-muted small">
                  {d.database_name}
                </span>
              </div>

              <table className="table table-sm table-bordered mb-3">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '5%' }}>#</th>
                    <th style={{ width: '25%' }}>Factor</th>
                    <th style={{ width: '40%' }}>Input</th>
                    <th style={{ width: '15%' }}>Effect</th>
                    <th style={{ width: '15%' }}>Running risk</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.steps || []).map((s, i) => (
                    <tr key={i}>
                      <td className="text-muted">{s.step}</td>
                      <td><strong>{s.name}</strong></td>
                      <td>
                        <code style={{ fontSize: '0.85em', color: '#0f172a' }}>
                          {JSON.stringify(s.input || {}, null, 0).replace(/[{}"]/g, '').replace(/,/g, ', ')}
                        </code>
                        {s.note && (
                          <div className="text-warning small mt-1">
                            <i className="fas fa-info-circle me-1"></i>{s.note}
                          </div>
                        )}
                      </td>
                      <td className="text-muted">{s.effect}</td>
                      <td>
                        <span className={getRiskBadgeClass(s.running_risk)}>
                          <i className="fas fa-circle fa-xs me-1"></i>{s.running_risk}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="card border-0" style={{ background: '#f8f9fa' }}>
                <div className="card-body py-2 small">
                  <div className="text-muted mb-1"><strong>Inputs used by this calculation:</strong></div>
                  <div className="row">
                    <div className="col-md-6">
                      <div>Privilege level: <code>{ins.privilege_level_name} ({ins.privilege_level})</code></div>
                      <div>Account status: <code>{ins.account_status || 'UNKNOWN'}</code></div>
                    </div>
                    <div className="col-md-6">
                      <div>Last login: <code>{ins.last_login || 'never'}</code>{ins.days_since_login != null && <span className="text-muted"> ({ins.days_since_login} days ago)</span>}</div>
                      <div>Has proxy grant: <code>{String(!!ins.has_proxy_grant)}</code></div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </WideModal>
    </div>
  );
};

export default OracleUserAccessReport;
